import { msalInstance } from '../index';
import { apiRequest } from '../authConfig';

const API_BASE = '/api/v1';

async function getAccessToken() {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (!account) return null;
    try {
        const response = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account,
        });
        return response.accessToken;
    } catch (err) {
        // Only attempt interactive if it's genuinely needed (not if another interaction is in progress)
        if (err.name === 'InteractionRequiredAuthError') {
            try {
                const response = await msalInstance.acquireTokenPopup(apiRequest);
                return response.accessToken;
            } catch {
                return null;
            }
        }
        return null;
    }
}

export async function fetchApi(path, options = {}) {
    const token = await getAccessToken();
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE}${path}`, {
        headers,
        ...options,
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
}

export async function fetchBlob(path, options = {}) {
    const token = await getAccessToken();
    const headers = { ...(options.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // headers last: an options.headers (e.g. Content-Type on a POST) must not replace the Authorization header
    const response = await fetch(`${API_BASE}${path}`, { ...options, headers });
    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `HTTP ${response.status}`);
    }
    return response.blob();
}

export const api = {
    dashboard: (params) => fetchApi(`/dashboard/national${params ? '?' + new URLSearchParams(params) : ''}`),
    reportsMtdSummary: (groupBy, month) => fetchApi(`/reports/mtd-summary?groupBy=${encodeURIComponent(groupBy)}&month=${encodeURIComponent(month)}`),
    reportsFindings: (month) => fetchApi(`/reports/findings?month=${encodeURIComponent(month)}`),
    reportsOpsOverview: () => fetchApi(`/reports/ops-overview`),
    dashboardTrends: (period) => fetchApi(`/dashboard/trends?period=${period || 'month'}`),
    dashboardGroupTrend: (groupBy, name, period) => fetchApi(`/dashboard/group-trend?groupBy=${groupBy}&name=${encodeURIComponent(name)}${period ? `&period=${period}` : ""}`),
    stores: () => fetchApi('/stores'),
    store: (siteId) => fetchApi(`/stores/${siteId}`),
    findings: (siteId, params) => fetchApi(`/stores/${siteId}/findings?${new URLSearchParams(params)}`),
    issues: (siteId) => fetchApi(`/stores/${siteId}/issues`),
    allIssues: (qs) => fetchApi(`/issues${qs || ''}`),
    issueCount: (qs) => fetchApi(`/issues/count${qs || ''}`),
    issueRplList: () => fetchApi(`/issues/rpl-list`),
    scans: () => fetchApi('/scans'),
    scanDetail: (siteId, scanHour) => fetchApi(`/scans/${siteId}/${scanHour}`),
    images: (siteId, params) => fetchApi(`/stores/${siteId}/images?${new URLSearchParams(params)}`),
    actionIssue: (id, data) => fetchApi(`/issues/${id}/action`, { method: 'POST', body: JSON.stringify(data) }),
    verificationStatus: (id) => fetchApi(`/issues/${id}/verification`),
    routingOptions: (siteId) => fetchApi(`/issues/routing-options?siteId=${siteId}`),
    createIssueFromFinding: (data) => fetchApi('/issues/create-from-finding', { method: 'POST', body: JSON.stringify(data) }),
    createIssueFromPreset: (data) => fetchApi('/issues/create-from-preset', { method: 'POST', body: JSON.stringify(data) }),
    verifyIssue: (id, data) => fetchApi(`/issues/${id}/verify`, { method: 'POST', body: JSON.stringify(data) }),
    reopenIssue: (id, data) => fetchApi(`/issues/${id}/reopen`, { method: 'POST', body: JSON.stringify(data) }),
    scheduleRecheck: (id, data) => fetchApi(`/issues/${id}/schedule-recheck`, { method: 'POST', body: JSON.stringify(data) }),
    cancelRecheck: (id) => fetchApi(`/issues/${id}/cancel-recheck`, { method: 'POST', body: JSON.stringify({}) }),
    resendEmail: (id) => fetchApi(`/issues/${id}/resend-email`, { method: 'POST', body: JSON.stringify({}) }),
    presetStores: () => fetchApi('/presets'),
    presetsByStore: (siteId) => fetchApi(`/presets/${siteId}`),
    updatePreset: (siteId, presetName, data) =>
        fetchApi(`/presets/${siteId}/${presetName}`, { method: 'PATCH', body: JSON.stringify(data) }),
    dropPreset: (siteId, presetName) =>
        fetchApi(`/presets/${siteId}/${presetName}`, { method: 'DELETE' }),
    clearUnlockedPresets: (siteId) =>
        fetchApi(`/presets/${siteId}`, { method: 'DELETE' }),
    duplicatePreset: (siteId, presetName) =>
        fetchApi(`/presets/${siteId}/${presetName}/duplicate`, { method: 'POST' }),
    nudgePreset: (siteId, presetName, delta) =>
        fetchApi(`/presets/${siteId}/${presetName}/nudge`, { method: 'POST', body: JSON.stringify(delta) }),
    confirmPreset: (siteId, presetName, ptz) =>
        fetchApi(`/presets/${siteId}/${presetName}/confirm`, { method: 'POST', body: JSON.stringify(ptz) }),
    togglePresetLock: (siteId, presetName) =>
        fetchApi(`/presets/${siteId}/${presetName}/lock`, { method: 'POST', body: JSON.stringify({}) }),
    health: () => fetchApi('/health'),
    cameraHealth: () => fetchApi('/cameras/health'),
    camerasForStore: (siteId) => fetchApi(`/cameras/store/${siteId}`),
    recheckNvr: (siteId) => fetchApi(`/cameras/${siteId}/recheck-nvr`, { method: 'POST' }),
    recheckPtz: (siteId) => fetchApi(`/cameras/${siteId}/recheck-ptz`, { method: 'POST' }),
    updateNvrIp: (siteId, ip) => fetchApi(`/cameras/${siteId}/nvr-ip`, { method: 'PUT', body: JSON.stringify({ ip }) }),
    updateNvrPassword: (siteId, password) => fetchApi(`/cameras/${siteId}/nvr-password`, { method: 'PUT', body: JSON.stringify({ password }) }),
    hourlyImages: (siteId, date) => fetchApi(`/stores/${siteId}/hourly-images?date=${date}`),
    fridgeSurveyImages: (siteId, date) => fetchApi(`/stores/${siteId}/fridge-survey-images${date ? `?date=${date}` : ''}`),
    storeIssues: (siteId, params) => fetchApi(`/stores/${siteId}/store-issues${params ? '?' + new URLSearchParams(params) : ''}`),
    storeReports: (siteId) => fetchApi(`/stores/${siteId}/reports`),

    // Preset notes
    addPresetNote: (siteId, data) => fetchApi(`/scans/${siteId}/preset-note`, { method: 'POST', body: JSON.stringify(data) }),
    deletePresetNote: (siteId, noteId) => fetchApi(`/scans/${siteId}/preset-note/${noteId}`, { method: 'DELETE' }),

    // Scan trigger
    triggerScan: (siteId) => fetchApi(`/scans/${siteId}/trigger`, { method: 'POST' }),

    // Scan reviews
    startReview: (siteId, scanHour, data) => fetchApi(`/scans/${siteId}/${scanHour}/review`, { method: 'POST', body: JSON.stringify(data) }),
    completeReview: (siteId, scanHour, data) => fetchApi(`/scans/${siteId}/${scanHour}/review/complete`, { method: 'POST', body: JSON.stringify(data) }),
    abandonReview: (siteId, scanHour, data) => fetchApi(`/scans/${siteId}/${scanHour}/review`, { method: 'DELETE', body: JSON.stringify(data) }),
    reviews: (params) => fetchApi(`/scans/reviews${params ? '?' + new URLSearchParams(params) : ''}`),
    reviewStats: (params) => fetchApi(`/scans/review-stats${params ? '?' + new URLSearchParams(params) : ''}`),

    // Video clips
    videoClipSites: () => fetchApi('/video-clips/sites'),
    videoClips: (params) => fetchApi(`/video-clips${params ? '?' + new URLSearchParams(params) : ''}`),
    videoClip: (id) => fetchApi(`/video-clips/${id}`),
    videoClipDownload: (id) => fetchApi(`/video-clips/${id}/download`),
    requestVideoClip: (data) => fetchApi('/video-clips', { method: 'POST', body: JSON.stringify(data) }),

    // Admin
    adminUsers: () => fetchApi('/admin/users'),
    adminPreAddUser: (data) => fetchApi('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
    adminAssignRole: (userId, data) => fetchApi(`/admin/users/${userId}/roles`, { method: 'POST', body: JSON.stringify(data) }),
    adminRemoveRole: (userId, assignmentId) => fetchApi(`/admin/users/${userId}/roles/${assignmentId}`, { method: 'DELETE' }),
    adminToggleActive: (userId, active) => fetchApi(`/admin/users/${userId}/active`, { method: 'PUT', body: JSON.stringify({ active }) }),
    adminRoles: () => fetchApi('/admin/roles'),
    adminUpdateRole: (roleId, permissionIds) => fetchApi(`/admin/roles/${roleId}`, { method: 'PUT', body: JSON.stringify({ permissionIds }) }),

    // POS Fraud review
    // Senior-only Fraud Report (pages.fraud_report). Downloads come back as blobs for the browser to save.
    fraudReport: {
        summary: (from, to) => fetchApi(`/fraud-report/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
        csv: (from, to) => fetchBlob(`/fraud-report/export.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
        draftSummary: (from, to, notes) => fetchApi('/fraud-report/summary', { method: 'POST', body: JSON.stringify({ from, to, notes }) }),
        pdf: (from, to, notes, summary, summary_model) => fetchBlob('/fraud-report/pdf', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ from, to, notes, summary, summary_model }) }),
        cameraFaults: (all) => fetchApi(`/fraud-report/camera-faults${all ? '?all=1' : ''}`),
        actionCameraFault: (id, action) => fetchApi(`/fraud-report/camera-faults/${id}/action`, { method: 'POST', body: JSON.stringify({ action_taken: action }) }),
        staffSync: (from, to) => fetchApi('/fraud-report/staff-sync', { method: 'POST', body: JSON.stringify({ from, to }) }),
    },
    // Dip Safety (dipwatch): review queue, site results (pages.dip_safety); dip point proposals (dipsafety.approve, Senior).
    dipSafety: {
        events: (params) => fetchApi(`/dip-safety/events${params ? "?" + new URLSearchParams(params) : ""}`),
        event: (id) => fetchApi(`/dip-safety/events/${encodeURIComponent(id)}`),
        review: (id, data) => fetchApi(`/dip-safety/events/${encodeURIComponent(id)}/review`, { method: "POST", body: JSON.stringify(data) }),
        results: (params) => fetchApi(`/dip-safety/results?${new URLSearchParams(params)}`),
        sites: () => fetchApi("/dip-safety/sites"),
        proposals: (params) => fetchApi(`/dip-safety/proposals${params ? "?" + new URLSearchParams(params) : ""}`),
        decide: (id, decision, note) => fetchApi(`/dip-safety/proposals/${encodeURIComponent(id)}/decision`, { method: "POST", body: JSON.stringify({ decision, note }) }),
    },
    posfraud: {
        list: (params) => fetchApi(`/posfraud${params ? '?' + new URLSearchParams(params) : ''}`),
        counts: (params) => fetchApi(`/posfraud/counts${params ? '?' + new URLSearchParams(params) : ''}`),
        exportCsv: (params) => fetchBlob(`/posfraud/export.csv${params ? '?' + new URLSearchParams(params) : ''}`),
        media: (id) => fetchApi(`/posfraud/${id}/media`),
        lines: (id) => fetchApi(`/posfraud/${id}/lines`),
        aiScore: (id) => fetchApi(`/posfraud/${id}/ai-score`),
        clip: (id) => fetchApi(`/posfraud/${id}/clip`),
        clipStatus: (url) => fetchApi(`/posfraud/clip-status?${new URLSearchParams({ url })}`),
        clipDownload: (id) => fetchApi(`/posfraud/${id}/clip?download=1`),
        review: (id, data) => fetchApi(`/posfraud/${id}/review`, { method: 'POST', body: JSON.stringify(data) }),
        // Reviewer re-pull through the edge API: { angle, observed_clip_start_nvr_local: 'YYYY-MM-DDTHH:MM:SS', reason }
        replacement: (id, data) => fetchApi(`/posfraud/edge/events/${id}/replacement`, { method: 'POST', body: JSON.stringify(data) }),
        // Re-pull a failed edge capture (no clip yet): { reason }
        recapture: (id, data) => fetchApi(`/posfraud/edge/events/${id}/recapture`, { method: 'POST', body: JSON.stringify(data || {}) }),
        deleteClip: (id) => fetchApi(`/posfraud/${id}/clip`, { method: 'DELETE' }),
        outcome: (id, data) => fetchApi(`/posfraud/${id}/outcome`, { method: 'PATCH', body: JSON.stringify(data) }),
        staffOptions: (id) => fetchApi(`/posfraud/${id}/staff-options`),
        staffLink: (id, data) => fetchApi(`/posfraud/${id}/staff`, { method: 'PATCH', body: JSON.stringify(data) }),
    },

    // Contacts
    contacts: () => fetchApi('/contacts'),

    // Workers (Azure Functions + Container Apps)
    workers: (params) => fetchApi(`/workers${params ? '?' + new URLSearchParams(params) : ''}`),

    // Email log
    emailLog: (params) => fetchApi(`/email/log${params ? '?' + new URLSearchParams(params) : ''}`),
    emailLogPending: () => fetchApi('/email/log/pending'),
    emailLogStats: () => fetchApi('/email/log/stats'),

    // Audit
    activityFeed: (params) => fetchApi(`/audit/activity${params ? '?' + new URLSearchParams(params) : ''}`),

    // Help docs
    helpDoc: (slug) => fetchApi(`/help/${slug}`),
    updateHelpDoc: (slug, data) => fetchApi(`/help/${slug}`, { method: 'PUT', body: JSON.stringify(data) }),

    // Monitoring
    monitoringFilters: () => fetchApi('/monitoring/filters'),
    monitoringHeatmap: (params) => fetchApi(`/monitoring/heatmap?${new URLSearchParams(params)}`),
    monitoringFixtureDetail: (params) => fetchApi(`/monitoring/fixture-detail?${new URLSearchParams(params)}`),

    // Observations (compliance checks during scan review)
    getObservations: (siteId, scanHour) => fetchApi(`/scans/${siteId}/${scanHour}/review/observations`),
    saveObservations: (siteId, scanHour, data) => fetchApi(`/scans/${siteId}/${scanHour}/review/observations`, {
        method: 'POST', body: JSON.stringify(data)
    }),

    // Dip monitoring
    dips: {
        sites: (params) => fetchApi(`/dips/sites${params ? '?' + new URLSearchParams(params) : ''}`),
        filters: () => fetchApi('/dips/filters'),
        cameras: (siteId) => fetchApi(`/dips/${siteId}/cameras`),
        savePreset: (siteId, data) => fetchApi(`/dips/${siteId}/preset`, {
            method: 'POST', body: JSON.stringify(data)
        }),
        deletePreset: (siteId, presetId) => fetchApi(`/dips/${siteId}/preset/${presetId}`, { method: 'DELETE' }),
        events: (params) => fetchApi(`/dips/events${params ? '?' + new URLSearchParams(params) : ''}`),
        reviewEvent: (eventId, data) => fetchApi(`/dips/events/${eventId}/review`, {
            method: 'POST', body: JSON.stringify(data)
        }),
        reviews: (params) => fetchApi(`/dips/reviews${params ? '?' + new URLSearchParams(params) : ''}`),
        reviewDetail: (id) => fetchApi(`/dips/reviews/${id}`),
        startReview: (id) => fetchApi(`/dips/reviews/${id}/start`, { method: 'POST' }),
        completeReview: (id, data) => fetchApi(`/dips/reviews/${id}/complete`, {
            method: 'POST', body: JSON.stringify(data)
        }),
        abandonReview: (id) => fetchApi(`/dips/reviews/${id}/abandon`, { method: 'POST' }),
        reviewVideoDownload: (id) => fetchApi(`/dips/reviews/${id}/video-download`),
        submissionVideoDownload: (id) => fetchApi(`/dips/submit/${id}/video-download`),
    },

    // Asset scanner
    assets: {
        scan: (data) => fetchApi('/assets/scan', { method: 'POST', body: JSON.stringify(data) }),
        siteAssets: (siteId) => fetchApi(`/assets/site/${siteId}`),
        scanHistory: (siteId) => fetchApi(`/assets/scan-history/${siteId}`),
        barcodeRequests: (params) => fetchApi(`/assets/barcode-requests${params ? '?' + new URLSearchParams(params) : ''}`),
        approveRequest: (id, data) => fetchApi(`/assets/barcode-requests/${id}/approve`, { method: 'POST', body: JSON.stringify(data) }),
        rejectRequest: (id, data) => fetchApi(`/assets/barcode-requests/${id}/reject`, { method: 'POST', body: JSON.stringify(data) }),
        classifications: () => fetchApi('/assets/classifications'),
        updateClassification: (id, data) => fetchApi(`/assets/classifications/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        deleteClassificationPhoto: (id) => fetchApi(`/assets/classifications/${id}/photo`, { method: 'DELETE' }),
    },

    // Onboarding wizard
    onboarding: {
        sites: (params) => fetchApi(`/onboarding/sites${params ? '?' + new URLSearchParams(params) : ''}`),
        filters: () => fetchApi('/onboarding/filters'),
        proposedLayout: (siteId) => fetchApi(`/onboarding/${siteId}/proposed-layout`),
        saveLayout: (siteId, data) => fetchApi(`/onboarding/${siteId}/layout`, {
            method: 'POST', body: JSON.stringify(data)
        }),
        presets: (siteId, batchId) => fetchApi(`/onboarding/${siteId}/presets?batchId=${batchId}`),
        nudge: (siteId, layoutId, delta) => fetchApi(`/onboarding/${siteId}/presets/${layoutId}/nudge`, {
            method: 'POST', body: JSON.stringify(delta)
        }),
        sweepNudge: (siteId, sweepId, delta) => fetchApi(`/onboarding/${siteId}/sweep/${sweepId}/nudge`, {
            method: 'POST', body: JSON.stringify(delta)
        }),
        confirm: (siteId, layoutId, ptz) => fetchApi(`/onboarding/${siteId}/presets/${layoutId}/confirm`, {
            method: 'POST', body: JSON.stringify(ptz)
        }),
        skip: (siteId, layoutId, reason) => fetchApi(`/onboarding/${siteId}/presets/${layoutId}/skip`, {
            method: 'POST', body: JSON.stringify({ reason })
        }),
        floorplan: (siteId) => fetchApi(`/onboarding/${siteId}/floorplan`),
        getCameraCalibration: (siteId) => fetchApi(`/onboarding/${siteId}/camera-calibration`),
        saveCameraCalibration: (siteId, data) => fetchApi(`/onboarding/${siteId}/camera-calibration`, {
            method: 'PUT', body: JSON.stringify(data)
        }),
    },

    // Fixture scan schedules
    fixtureSchedules: {
        load: () => fetchApi('/fixture-schedules'),
        createSchedule: (data) => fetchApi('/fixture-schedules/schedules', { method: 'POST', body: JSON.stringify(data) }),
        updateSchedule: (id, data) => fetchApi(`/fixture-schedules/schedules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        deleteSchedule: (id) => fetchApi(`/fixture-schedules/schedules/${id}`, { method: 'DELETE' }),
        createSubtype: (data) => fetchApi('/fixture-schedules/subtypes', { method: 'POST', body: JSON.stringify(data) }),
        updateSubtype: (id, data) => fetchApi(`/fixture-schedules/subtypes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        deleteSubtype: (id) => fetchApi(`/fixture-schedules/subtypes/${id}`, { method: 'DELETE' }),
        updateType: (code, data) => fetchApi(`/fixture-schedules/types/${code}`, { method: 'PUT', body: JSON.stringify(data) }),
    },
};
